import {List} from 'immutable';
import type {Map} from 'immutable';
import {GenericListCollectionModel} from '../Generic/GenericCollection.model';
import {stats, BUCKETS} from 'lib/StatsUtil';
import type {MasteryBucket} from 'lib/StatsUtil';
import type {StudentModelV2} from 'resources/GeneratedModels/Student/StudentModel.v2';

/**
 * The runtime collection is a List<StudentModelV2> augmented (in
 * StudentsListCollectionModel below) with `asMap`/`cache` from the generic
 * collection plus the bucket/aggregate methods declared on `methods`. This
 * interface describes that augmented shape so both `this` inside the methods
 * and consumers of {@link StudentsListCollectionModel} see the added members.
 */
export interface StudentsListCollection extends List<StudentModelV2> {
  cache: <T>(key: string, func: () => T) => T;
  asMap: () => Map<string, StudentModelV2>;
  getStrugglingStudents: () => List<StudentModelV2>;
  getPassingStudents: () => List<StudentModelV2>;
  getProficientStudents: () => List<StudentModelV2>;
  getExcellingStudents: () => List<StudentModelV2>;
  getMasteryStudents: () => List<StudentModelV2>;
  getActiveStudents: () => List<StudentModelV2>;
  getActiveStudentsCount: () => number;
  getInactiveStudentsCount: () => number;
  getTotalNumberOfAttempts: () => number;
  getTotalPointsEarned: () => number;
  getActiveStudentsAverageAccuracy: (opts?: {round: boolean | null | undefined}) => number;
  getTotalTimeElapsed: () => number;
  sortedByAccuracy: () => StudentsListCollection;
  getMaxNumberOfAttempts: () => number;
}

function makeBucketFilterFunc(bucket: MasteryBucket): () => List<StudentModelV2> {
  return function(this: StudentsListCollection): List<StudentModelV2> {
    return this.filter((student) => {
      return (
        student.getMeta().getCountOfGuesses() > 0 &&
        stats.inBucket(bucket, student.getAccuracyFromMeta())
      );
    }) as List<StudentModelV2>;
  };
}

const methods = {
   
  getStrugglingStudents: makeBucketFilterFunc(BUCKETS.STRUGGLING),
  getPassingStudents: makeBucketFilterFunc(BUCKETS.PASSING),
  getProficientStudents: makeBucketFilterFunc(BUCKETS.PROFICIENT),
  getExcellingStudents: makeBucketFilterFunc(BUCKETS.EXCELLING),
  getMasteryStudents: makeBucketFilterFunc(BUCKETS.MASTERY),

  getActiveStudents(this: StudentsListCollection): List<StudentModelV2> {
    return this.filter(
      (student) => student.getMeta().getCountOfGuesses() > 0
    ) as List<StudentModelV2>;
  },

  getActiveStudentsCount(this: StudentsListCollection): number {
    return this.getActiveStudents().count();
  },

  getInactiveStudentsCount(this: StudentsListCollection): number {
    return this.size - this.getActiveStudentsCount();
  },

  getTotalNumberOfAttempts(this: StudentsListCollection): number {
    return this.reduce((acc, student) => acc + student.getMeta().getCountOfGuesses(), 0);
  },

  getTotalPointsEarned(this: StudentsListCollection): number {
    return this.reduce((acc, student) => acc + student.getMeta().getSumOfGuessesPointsEarned(), 0);
  },

  getActiveStudentsAverageAccuracy(
    this: StudentsListCollection,
    {round}: {round: boolean | null | undefined} = {round: true}
  ): number {
    const accuracies = this.getActiveStudents()
      .map((student) => student.getAccuracyFromMeta())
      .toArray();
    const average = stats.average(...accuracies);
    return round ? Math.round(average) : average;
  },

  getTotalTimeElapsed(this: StudentsListCollection): number {
    return this.reduce((acc, student) => acc + student.getMeta().getSumOfGuessesTimeElapsed(), 0);
  },

  sortedByAccuracy(this: StudentsListCollection): StudentsListCollection {
    /**
     * 'raw_accuracy' is not somethign we can currently sort by, so if we call this method,
     * we'll sort on the front-end. Also, because sortBy returns a new List, we'll need to
     * cast it into the StudentsListCollectionModel again
     */
    const students = this.sortBy(function(student): number {
      return student.getAccuracyFromMeta() * -1;
    }) as List<StudentModelV2>;
    // StudentsListCollectionModel is a factory that returns its own object, so
    // calling it directly is equivalent to the historical `new` invocation.
    return StudentsListCollectionModel(students);
  },

  getMaxNumberOfAttempts(this: List<StudentModelV2>): number {
    const studentWithMaxNumberOfAttempts = this.maxBy((student) =>
      student.getMeta().getMaxAttemptNumber()
    );
    return studentWithMaxNumberOfAttempts
      ? studentWithMaxNumberOfAttempts.getMeta().getMaxAttemptNumber()
      : 0;
  }
   
};

export function StudentsListCollectionModel(
  students: List<StudentModelV2> = List()
): StudentsListCollection {
  // GenericListCollectionModel returns a List augmented with a `cache` slot;
  // we then bolt on the bucket/aggregate methods, so treat it as the augmented
  // collection (with a string index for the dynamic method assignment below).
  const collection = GenericListCollectionModel(students) as StudentsListCollection & {
    [methodName: string]: unknown;
  };

  Object.keys(methods).forEach(function(methodName) {
    collection[methodName] = function(this: StudentsListCollection): any {
      const method = (methods as Record<string, (this: StudentsListCollection) => unknown>)[
        methodName
      ].bind(collection);
      if (collection.isEmpty()) {
        // If our collection is empty (e.g. if the data hasn't resolved), we'll return the normal method call since we don't want
        // to cache the result of our function call that was called on empty data
        return method();
      }
      return this.cache(methodName, method);
    };
  });

  return collection;
}
